import { ChangeDetectionStrategy, ChangeDetectorRef, Component, forwardRef, Input, OnDestroy, OnInit, inject, input, output } from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { debounceTime, first, map, pairwise, startWith, Subject, take, takeUntil, takeWhile } from 'rxjs';
import { filter } from 'rxjs/internal/operators/filter';
import { IdValue } from '../../../../models';
import { disableFormControlsBasedOnStatus, FeatureFlagRepository, SessionQuery, SessionStore } from '../../../../../core/state';
import { Cmg1003LoanOfficerQuery, Cmg1003LoanQuery, Cmg1003LookupsQuery, Cmg1003StoreQueryService } from '../../../../../loan-state';
import { getUniqueId, resetValidations } from '../../../../utils';
import { BorrowerAddressOwnershipTypes, BorrowerAddressTypes } from '../../../../../cmg-1003/models/borrower-address-types.enum';
import { AddressModel } from '../../../../../cmg-1003/models/cmg-1003-common.model';
import { countriesWithSelectOption, statesWithNoSelect } from '../../../../../cmg-1003/models/lookups/common.lookups';
import { ZipcodeModel } from '../../../../../cmg-1003/models/lookups/zip-code.model';
import { Cmg1003SectionTypes } from '../../../../../cmg-1003/models/section-types.enum';
import { Cmg1003LookupsService } from '../../../../../cmg-1003/services/cmg-1003-lookups.service';
import { CmgUiUtilities } from '../../../../../cmg-1003/components/common/cmg-ui-utils';
import { Cmg1003SubSectionTypes } from '../../../../../cmg-1003/models/sub-section-types.enum';
import { NotificationService } from 'apps/cmg-clear2-ui/src/app/services';
import { SpinnerService } from '../../../../services';
import { LoanBannerService } from 'apps/cmg-clear2-ui/src/app/loan-banner/services/loan-banner.service';
import { LoanOfficerModel } from 'apps/cmg-clear2-ui/src/app/cmg-1003/models';
import { DialogService } from 'primeng/dynamicdialog';
import { ConfirmationModalComponent } from 'apps/cmg-clear2-ui/src/app/core';
import { ConfirmationModalCloseType } from 'apps/cmg-clear2-ui/src/app/models';
import { InputText } from 'primeng/inputtext';
import { CmgFormControlDomDirective } from '../../../../directives/cmg-form-control-dom.directive';
import { CmgValidatorMessagesComponent } from '../../../cmg-validator-messages/cmg-validator-messages.component';
import { CmgFormControlZipCodeComponent } from '../../../cmg-form-control-zip-code/cmg-form-control-zip-code.component';
import { Select } from 'primeng/select';
import { AsyncPipe } from '@angular/common';
import { InputNumber } from 'primeng/inputnumber';
import { CmgPrimeNgInputNumberFocusDirective } from '../../../../directives/cmg-input-number-focus.directive';
import { QaSlugPipe } from '../../../../pipes/qa-slug.pipe';

@Component({
  selector: 'cmg-borrower-address-child',
  templateUrl: './borrower-address-child.component.html',
  styleUrls: ['./borrower-address-child.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => BorrowerAddressChildComponent),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    InputText,
    CmgFormControlDomDirective,
    CmgValidatorMessagesComponent,
    CmgFormControlZipCodeComponent,
    Select,
    InputNumber,
    CmgPrimeNgInputNumberFocusDirective,
    AsyncPipe,
    QaSlugPipe,
  ],
})
export class BorrowerAddressChildComponent implements OnInit, ControlValueAccessor, OnDestroy {
  private readonly _formBuilder = inject(UntypedFormBuilder);
  private readonly _lookupsService = inject(Cmg1003LookupsService);
  private readonly _sessionQuery = inject(SessionQuery);
  private readonly _sessionStore = inject(SessionStore);
  private readonly _storeQuery = inject(Cmg1003StoreQueryService);
  private readonly _dialogService = inject(DialogService);
  private readonly _cdRef = inject(ChangeDetectorRef);
  private readonly _lookupsQuery = inject(Cmg1003LookupsQuery);
  private readonly _loanOfficerQuery = inject(Cmg1003LoanOfficerQuery);
  private readonly _notificationService = inject(NotificationService);
  private readonly _loanInfoQuery = inject(Cmg1003LoanQuery);
  private readonly _featureFlagRepo = inject(FeatureFlagRepository);
  private readonly _spinnerService = inject(SpinnerService);
  private readonly _loanBannerService = inject(LoanBannerService);

  private _addressModelOriginal: AddressModel;
  private _destroyed$: Subject<boolean> = new Subject<boolean>();

  readonly isMailing = input(false);

  readonly previousAddressMode = input(false);

  _sameAsPresentAddress = false;
  get sameAsPresentAddress() {
    return this._sameAsPresentAddress;
  }

  @Input() set sameAsPresentAddress(value: boolean) {
    // Will get in the setter here when user clicks 'Copy from Borrower' from BorrowerDetailsComponent
    if (this._sameAsPresentAddress !== value) {
      this._sameAsPresentAddress = value;
      this.onSameAsPresentAddressChanged();
    }
  }

  readonly presentAddress = input<AddressModel>(undefined);

  /** string that describes where this address is instantiated. */
  readonly context = input('Borrower address');

  readonly addressType = input<BorrowerAddressTypes>(undefined);

  /** For QA. The automation ID prefix, formatted as "borrowers-[index]-[property name]-". */
  readonly autoIdPrefix = input<string>(undefined);

  readonly cancelClicked = output();

  readonly addOrUpdateClicked = output();

  readonly mailingAddressSameAsPresentAddressClicked = output<boolean>();

  readonly borrowerId = input<number>(undefined);

  states = [new IdValue(null, 'Select', 'Select'), ...statesWithNoSelect];
  countries = countriesWithSelectOption;
  livingStatusOptions$ = this._lookupsQuery.select((e) => e.livingStatusTypes);

  BorrowerAddressTypes = BorrowerAddressTypes;
  form: UntypedFormGroup;
  uniqueId = getUniqueId();

  statusRequiresFullDisabling$ = this._storeQuery.statusRequiresFullDisabling$();

  private _statusRequiresFullDisabling;
  private _isDisabled = false;
  private _fileName: string;
  loanOfficerModel: LoanOfficerModel;

  get value(): AddressModel {
    return this.form.value;
  }

  set value(value: AddressModel) {
    this._addressModelOriginal = value;
    this._updateAddressForm(value);
    this._onChange(value);
    this._onTouched();
  }

  private _updateAddressForm(addressModel: AddressModel) {
    if (addressModel) {
      this.form.get('addressType').setValue(addressModel?.addressType === 'NotAssigned' ? null : addressModel?.addressType);
      this.form.get('street').setValue(addressModel?.street);
      this.form.get('state').setValue(addressModel?.state);
      this.form.get('countryCode').setValue(addressModel?.countryCode);
      this.form.get('zipCode').setValue(addressModel?.zipCode);
      this.form.get('city').setValue(addressModel?.city);
      this.form.get('years').setValue(addressModel?.years);
      this.form.get('months').setValue(addressModel?.months);
      this.form.get('monthlyRent').setValue(addressModel?.monthlyRent);
    }

    this.form.markAsPristine();
    this.form.markAsUntouched();
  }

  get streetControl() {
    return this.form.controls.street;
  }

  get stateControl() {
    return this.form.controls.state;
  }

  get cityControl() {
    return this.form.controls.city;
  }

  get zipCodeControl() {
    return this.form.controls.zipCode;
  }

  get mode() {
    return this._addressModelOriginal?.street === undefined ||
      this._addressModelOriginal?.street === null ||
      this._addressModelOriginal?.street === ''
      ? 'add'
      : 'update';
  }

  ngOnInit(): void {
    // create the inner form
    this.form = this._formBuilder.group(
      {
        addressType: [null, Validators.required],
        street: [null, Validators.required],
        city: [null, Validators.required],
        state: [null, Validators.required],
        countryCode: ['US', Validators.required],
        zipCode: new UntypedFormControl(null, {
          validators: !this.previousAddressMode() ? [Validators.required] : null,
        }),
        monthlyRent: [],
        years: [null],
        months: [null],
        sameAsPresentAddress: [false, { updateOn: 'change' }],
      },
      { updateOn: 'blur' },
    );

    if (!this.isMailing()) {
      this.form
        .get('addressType')
        .valueChanges.pipe(takeUntil(this._destroyed$))
        .subscribe((t) => {
          this._onAddressTypeChanged(t);
        });
    }

    disableFormControlsBasedOnStatus([this.form], this._storeQuery);

    this._setupContinuousValidation();

    if (!this.previousAddressMode()) {
      this._setupContinuousUpdate();
    }

    this._storeQuery
      .statusRequiresFullDisabling$()
      .pipe(take(1))
      .subscribe((disable) => {
        this._statusRequiresFullDisabling = disable;
      });

    if (this.addressType() === BorrowerAddressTypes.Present) {
      this._loanInfoQuery
        .select((l) => l?.fileName)
        .pipe(takeWhile((name) => !name || name === '', true))
        .subscribe((fileName) => {
          this._fileName = fileName;
        });

      this._loanOfficerQuery
        .select()
        .pipe(
          filter((lo) => !!lo && lo.partyId > 0),
          first(),
        )
        .subscribe((lo) => (this.loanOfficerModel = lo));
    }
  }

  ngOnDestroy(): void {
    this._destroyed$.next(true);
    this._destroyed$.complete();
  }

  onZipCodeChanged(event: any) {
    const zipCode = event?.target?.value;
    this._onZipCodeChanged(zipCode);
  }

  private _setupContinuousUpdate() {
    this._sessionQuery
      .select((s) => s.all1003Loaded)
      .pipe(
        filter((all1003Loaded) => all1003Loaded === true),
        debounceTime(500),
        take(1),
      )
      .subscribe(() => {
        this.form.valueChanges
          .pipe(
            startWith(this.form.value),
            debounceTime(0),
            pairwise(),
            map(([oldValues, newValues]) => {
              const key = Object.keys(newValues).find((k) => newValues[k] !== oldValues[k]);
              return {
                key: key,
                oldValue: oldValues[key],
                newValue: newValues[key],
              };
            }),
            filter((t) => !!t.key),
            takeUntil(this._destroyed$),
          )
          .subscribe(() => {
            this.addOrUpdate();
          });
      });
  }

  private _setupContinuousValidation() {
    this._sessionQuery
      .select((t) => t.hasAusPanelBeenOpened)
      .pipe(takeUntil(this._destroyed$))
      .subscribe((isAusPanelOpened) => {
        if (isAusPanelOpened && !this.previousAddressMode()) {
          this._sessionQuery
            .select((s) => s.all1003Loaded)
            .pipe(
              filter((all1003Loaded) => all1003Loaded === true),
              debounceTime(500),
              take(1),
            )
            .subscribe(() => {
              this.form.valueChanges.pipe(debounceTime(500), takeUntil(this._destroyed$)).subscribe(() => {
                this._triggerValidation();
              });
            });
        }
      });
  }

  setDisabledState?(isDisabled: boolean) {
    this._isDisabled = isDisabled;
    this._isDisabled ? this.form.disable() : this.form.enable();
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private _onChange: any = () => {};

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private _onTouched: any = () => {};

  registerOnChange(fn) {
    this._onChange = fn;
  }

  writeValue(value) {
    this.value = value;

    if (value === null) {
      this.form.reset();
      this.form.get('countryCode').setValue('US', { emitEvent: false, onlySelf: true });
    }
  }

  registerOnTouched(fn) {
    this._onTouched = fn;
  }

  validate() {
    if (!this.previousAddressMode()) {
      this._validateYearsAndMonths();
      this.form.markAllAsTouched();
      this.form.markAsDirty();
    }

    this._buildValidationErrors();
  }

  resetValidations(): void {
    resetValidations(this.form);

    // todo this.zipCode.resetValidations

    setTimeout(() => {
      this._cdRef.detectChanges();
    }, 100);
  }

  addOrUpdate() {
    if (this.previousAddressMode()) {
      this._validateYearsAndMonths();

      if (!this.form.get('zipCode').value) {
        this.form.get('zipCode').setValidators(Validators.required);
        this.form.get('zipCode').updateValueAndValidity();
      }

      if (this.form.valid) {
        const updatedAddressModel = { ...this._addressModelOriginal, ...this.form.value };
        updatedAddressModel.zipCode = updatedAddressModel.zipCode?.replace('-', '');

        this._onChange(updatedAddressModel);
        this._onTouched();
        this.addOrUpdateClicked.emit();
      } else {
        this.form.markAllAsTouched();
        this.form.markAsDirty();
      }
    } else {
      const updatedAddressModel = { ...this._addressModelOriginal, ...this.form.value };
      updatedAddressModel.zipCode = updatedAddressModel.zipCode?.replace('-', '');

      this._onChange(updatedAddressModel);
      this._onTouched();
      this._triggerValidation();
      this._addressModelOriginal = updatedAddressModel;
    }
  }

  private _triggerValidation() {
    this._validateYearsAndMonths();
    this.form.markAllAsTouched();
    this.form.markAsDirty();
    this._buildValidationErrors();
  }

  clearOrCancel() {
    if (!this._statusRequiresFullDisabling && this.mode === 'update') {
      const dialogRef = this._dialogService.open(ConfirmationModalComponent, {
        closable: true,
        width: '512px',
        data: {
          message: `Are you sure you want to clear the changes in ${this.context()} section?`,
          header: 'Confirmation',
          icon: 'pi pi-info-circle',
          acceptLabel: 'Yes',
          rejectLabel: 'No',
        },
      });

      dialogRef.onClose.subscribe((value: ConfirmationModalCloseType) => {
        if (value === ConfirmationModalCloseType.ClosedViaAcceptButton) {
          this._updateAddressForm(this.context().includes('Previous') ? new AddressModel() : this._addressModelOriginal);
          this.cancelClicked.emit();
        }
      });
    } else {
      // in add mode (or the form is disabled because of the loan status) - just clear the fields
      this._updateAddressForm(new AddressModel());
      this._addressModelOriginal = null;

      this._storeQuery
        .getBorrowersBySelectedApplicationId()
        .pipe(map((borrowers) => borrowers.find((borrower) => this.borrowerId() === borrower.borrowerId)))
        .pipe(take(1))
        .subscribe((borrower) => {
          const subSection = this._getSubsection(borrower.isPrimaryBorrower);
          this._sessionStore.clearSectionValidationWarnings(Cmg1003SectionTypes.borrowerInformation, subSection);
        });
    }
  }

  onSameAsPresentAddressChanged() {
    this._sameAsPresentAddress = !this._sameAsPresentAddress;

    this.form.get('sameAsPresentAddress').setValue(this._sameAsPresentAddress, { emitEvent: false, onlySelf: true });

    if (this._sameAsPresentAddress) {
      this.form.get('street').disable({ emitEvent: false, onlySelf: true });
      this.form.get('city').disable({ emitEvent: false, onlySelf: true });
      this.form.get('state').disable({ emitEvent: false, onlySelf: true });
      this.form.get('countryCode').disable({ emitEvent: false, onlySelf: true });
      this.form.get('zipCode').disable({ emitEvent: false, onlySelf: true });
    } else {
      this.form.get('street').enable({ emitEvent: false, onlySelf: true });
      this.form.get('city').enable({ emitEvent: false, onlySelf: true });
      this.form.get('state').enable({ emitEvent: false, onlySelf: true });
      this.form.get('countryCode').enable({ emitEvent: false, onlySelf: true });
      this.form.get('zipCode').enable({ emitEvent: false, onlySelf: true });
    }

    this.mailingAddressSameAsPresentAddressClicked?.emit(this._sameAsPresentAddress);
  }

  private _validateYearsAndMonths() {
    if (!this.previousAddressMode()) {
      !this.form.get('years').value ? this.form.get('years').setValidators(Validators.required) : this.form.get('years').clearValidators();
      !this.form.get('months').value ? this.form.get('months').setValidators(Validators.required) : this.form.get('months').clearValidators();
      this.form.get('months').updateValueAndValidity({ emitEvent: false });
      this.form.get('years').updateValueAndValidity({ emitEvent: false });
      this.form.get('months').markAsDirty();
      this.form.get('months').markAsTouched();
      this.form.get('years').markAsDirty();
      this.form.get('years').markAsTouched();
    }
  }

  private _onAddressTypeChanged(t: BorrowerAddressOwnershipTypes): void {
    if (t !== 'Rent') {
      this.form.get('monthlyRent').setValue(null);
    }
  }

  private readonly _onZipCodeChanged = (zipCode: string) => {
    if (!this._sameAsPresentAddress) {
      // When _sameAsPresentAddress is set(only set in mailing & is false in other places)
      // zipcode is copied over and we don't want to overwrite the city and state that
      // we got from present address
      this._lookupsService
        .getCityStateByZipCode(zipCode)
        .pipe(take(1))
        .subscribe((zipCodeModel: ZipcodeModel) => {
          if (zipCodeModel.zipCodeID) {
            this.form.get('city').setValue(zipCodeModel.city);
            this.form.get('state').setValue(zipCodeModel.state);
          }
        });
    }
  };

  private _buildValidationErrors() {
    this._storeQuery
      .getBorrowersBySelectedApplicationId()
      .pipe(map((borrowers) => borrowers.find((borrower) => this.borrowerId() === borrower.borrowerId)))
      .pipe(take(1))
      .subscribe((borrower) => {
        const errorPrefix = CmgUiUtilities.getBorrowerNameErrorPrefix(
          borrower?.isPrimaryBorrower,
          borrower?.firstName,
          borrower?.lastName,
          borrower?.suffix,
        );

        const controlsWithErrors = CmgUiUtilities.getControlsWithErrors(this.form);
        const invalidControls = CmgUiUtilities.getInvalidControlModels(controlsWithErrors);

        invalidControls.forEach((t) => {
          if (!(this.isMailing() && (t.formControlName === 'years' || t.formControlName === 'months'))) {
            switch (t.formControlName) {
              case 'addressType':
                if (t.errors.required) {
                  t.invalidMessage = `${errorPrefix}: ${BorrowerAddressTypes[this.addressType()]} Address Living Status`;
                }
                break;
              case 'street':
                if (t.errors.required) {
                  t.invalidMessage = `${errorPrefix}: ${BorrowerAddressTypes[this.addressType()]} Address Street`;
                }
                break;
              case 'city':
                if (t.errors.required) {
                  t.invalidMessage = `${errorPrefix}: ${BorrowerAddressTypes[this.addressType()]} Address City`;
                }
                break;
              case 'state':
                if (t.errors.required) {
                  t.invalidMessage = `${errorPrefix}: ${BorrowerAddressTypes[this.addressType()]} Address State`;
                }
                break;
              case 'countryCode':
                if (t.errors.required) {
                  t.invalidMessage = `${errorPrefix}: ${BorrowerAddressTypes[this.addressType()]} Address Country`;
                }
                break;
              case 'zipCode':
                if (t.errors.required) {
                  t.invalidMessage = `${errorPrefix}: ${BorrowerAddressTypes[this.addressType()]} Address Zip`;
                }
                break;
              case 'years':
                if (t.errors.required) {
                  t.invalidMessage = `${errorPrefix}: ${BorrowerAddressTypes[this.addressType()]} Address Years`;
                }
                break;
              case 'months':
                if (t.errors.required) {
                  t.invalidMessage = `${errorPrefix}: ${BorrowerAddressTypes[this.addressType()]} Address Months`;
                }
                break;
              default:
                break;
            }
          }
        });

        // TODO Find out why borrower is null here sometimes. Happens when borrowerId is negative.
        const subSection = borrower ? this._getSubsection(borrower.isPrimaryBorrower) : null;
        this._sessionStore.setValidationError(Cmg1003SectionTypes.borrowerInformation, invalidControls, subSection);

        // This ensures validation styling is applied after everything is checked.
        this._cdRef.detectChanges();
      });
  }

  private _getSubsection(isPrimaryBorrower: boolean): Cmg1003SubSectionTypes {
    let subSection = Cmg1003SubSectionTypes.primaryBorrowerPresentAddress;
    const previousAddressMode = this.previousAddressMode();
    const isMailing = this.isMailing();
    if (isPrimaryBorrower && isMailing) {
      subSection = Cmg1003SubSectionTypes.primaryborrowerMailingAddress;
    } else if (isPrimaryBorrower && previousAddressMode) {
      subSection = Cmg1003SubSectionTypes.primaryBorrowerPreviousAddress;
    } else if (!isPrimaryBorrower && !isMailing && !previousAddressMode) {
      subSection = Cmg1003SubSectionTypes.additionalBorrowerPresentAddress;
    } else if (!isPrimaryBorrower && isMailing) {
      subSection = Cmg1003SubSectionTypes.additionalBorrowerMailingAddress;
    } else if (!isPrimaryBorrower && previousAddressMode) {
      subSection = Cmg1003SubSectionTypes.additionalBorrowerPreviousAddress;
    }

    return subSection;
  }
}
